// Goalie freeze — the goalie covers a slow puck near her body, stopping play.
//
// Owns:   the trigger that turns a "slow puck near a non-pulled goalie that
//         just touched it" into a Stoppage at the end-zone dot on the
//         puck's side.
// Reads:  state.puck (pos / vel / mode / lastTouchedBy), state.goalies
//         (pos / mode / pulled), tuning.goalie.freezeMaxSpeed / freezeRange.
// Mutates: goalie.mode → Saving (so the cover animation reads), goalie.modeTimer,
//          and (via enterStoppage) state.puck / state.clock / state.rules.
// Emits:  Stoppage(reason="GoalieFreeze"). The matching Faceoff event fires
//         from the Stoppage → Faceoff transition in `core/SimTick.ts` Step 18.
//
// Why `lastTouchedBy === goalie.id` is the right gate:
//   In `goalie/save-volumes.ts:resolveGoalieSaves`, every goalie-body contact
//   sets `puck.lastTouchedBy = goalie.id`. So the gate fires for:
//     - a slow pass deflecting off the body (restitution 0.4 → 1.6 m/s
//       outward for a 4 m/s incoming pass; below freezeMaxSpeed) — covers.
//     - a fresh pad rebound from a fast shot (always rebounds at ≥4 m/s
//       outward) — doesn't fire on the save tick, and integration carries
//       the puck out past freezeRange before drag slows it to threshold.
//     - a slow rebound that's drifted back to the goalie body — covers.
//   Pucks the goalie hasn't touched (e.g. a teammate's drop pass in the
//   crease) won't freeze — she only grabs what's "hers."
//
// Zero-allocation.

import { defensiveZoneSpots, nearestFaceoffSpot } from "../state/faceoff-spots.js";
import { tuning } from "../state/tuning.js";
import type { MatchState } from "../state/types.js";
import { enterStoppage } from "./stoppage.js";

export function tickGoalieFreeze(state: MatchState): void {
  if (state.clock.phase !== "Play" && state.clock.phase !== "Overtime") return;
  if (state.puck.mode === "Controlled") return;
  if (state.puck.lastTouchedBy === null) return;

  const vx = state.puck.vel.x;
  const vy = state.puck.vel.y;
  const maxSpeed = tuning.goalie.freezeMaxSpeed;
  if (vx * vx + vy * vy >= maxSpeed * maxSpeed) return;

  const range = tuning.goalie.freezeRange;
  for (const goalie of state.goalies) {
    if (goalie.pulled) continue;
    if (goalie.mode === "Recovering") continue;
    if (state.puck.lastTouchedBy !== goalie.id) continue;

    const dx = state.puck.pos.x - goalie.pos.x;
    const dy = state.puck.pos.y - goalie.pos.y;
    if (dx * dx + dy * dy > range * range) continue;

    // She covers it. Promote to Saving so the renderer can show the
    // glove-down pose, then route into a Stoppage at the DZ dot on the
    // puck's side.
    goalie.mode = "Saving";
    goalie.modeTimer = tuning.goalie.reactionTicks;
    const spot = nearestFaceoffSpot(
      state.puck.pos.x,
      state.puck.pos.y,
      defensiveZoneSpots(goalie.teamId),
    );
    enterStoppage(state, spot, "GoalieFreeze");
    return;
  }
}
